import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const
export type PageSize = typeof PAGE_SIZE_OPTIONS[number] | 'all'
export const DEFAULT_PAGE_SIZE: PageSize = 100

function parsePageSize(raw: string | null): PageSize | undefined {
  if (raw === 'all') return 'all'
  const n = raw ? parseInt(raw, 10) : NaN
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? (n as PageSize) : undefined
}

// Z56 (page-size persistence) + Z35.1 (URL params): shared page-size + page-index state for
// list pages. `key` should be unique per page (e.g. 'jobs-list', 'customers-list') -- used
// both as the localStorage key and as a namespace note: this hook reads/writes the *global*
// `?page=&size=` query params, so a route with two independent paginated lists would collide;
// none currently do.
//
// Source of truth is the URL: `page`/`pageSize` are derived from searchParams on every render,
// and setPage/setPageSize just update the URL (react-router re-renders with the new derived
// values) -- this sidesteps the usual two-way-binding-with-the-URL bugs of also keeping a
// mirrored useState in sync with it.
//
// Precedence for pageSize when the URL has no `size` param: this browser's last choice for
// this specific list (localStorage), else the page's own default. A URL with an explicit
// `?size=` always wins -- so a shared link forces that page size for whoever opens it,
// regardless of their own remembered preference.
export function usePagination(key: string, defaultPageSize: PageSize = DEFAULT_PAGE_SIZE) {
  const storageKey = `pageSize:${key}`
  const [searchParams, setSearchParams] = useSearchParams()

  const pageSize: PageSize = useMemo(() => {
    const fromUrl = parsePageSize(searchParams.get('size'))
    if (fromUrl !== undefined) return fromUrl
    try {
      const fromStorage = parsePageSize(localStorage.getItem(storageKey))
      if (fromStorage !== undefined) return fromStorage
    } catch {
      // private window / storage blocked -- fall through to default
    }
    return defaultPageSize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, storageKey])

  // URL page is 1-based (human-facing, matches the "Page X of Y" display); internal/range
  // math stays 0-based.
  const page: number = useMemo(() => {
    const raw = parseInt(searchParams.get('page') || '', 10)
    return Number.isFinite(raw) && raw >= 1 ? raw - 1 : 0
  }, [searchParams])

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(pageSize))
    } catch {
      // private window / storage blocked -- fine, just won't persist this session
    }
  }, [storageKey, pageSize])

  function setPage(next: number) {
    // Idempotency guard: several existing pages do `useEffect(() => setPage(0), [...filters,
    // setPage])` -- since setPage is a plain function (a new reference every render, same as
    // the useState setter this replaced), that effect re-runs on every render. A useState
    // setter silently bails out when set to its current value; setSearchParams does not, so
    // without this check that pattern would call setSearchParams (and re-render) every render
    // -- exactly the kind of infinite-refetch loop Z61 already burned us on once for `range`.
    if (next === page) return
    setSearchParams(prev => {
      const p = new URLSearchParams(prev)
      if (next <= 0) p.delete('page')
      else p.set('page', String(next + 1))
      return p
    }, { replace: true })
  }

  function setPageSize(next: PageSize) {
    if (next === pageSize) return
    setSearchParams(prev => {
      const p = new URLSearchParams(prev)
      if (next === defaultPageSize) p.delete('size')
      else p.set('size', String(next))
      p.delete('page') // changing page size always resets to page 1
      return p
    }, { replace: true })
  }

  // .range() bounds for the current page -- undefined when pageSize is 'all' (no range applied,
  // caller fetches everything; only appropriate for tables Gary has confirmed are small).
  //
  // Z61: this MUST be memoized. A plain array literal here gets a new identity every render;
  // callers pass `range` into a useCallback's deps (e.g. `fetchCustomers`), and a page whose
  // fetch effect is keyed on that callback's identity (`useEffect(() => setTimeout(fetch, ...),
  // [fetch])`) then refetches -> re-renders -> gets a "new" range -> refetches forever. That
  // was the Companies/Ports/Carriers page continuous-flicker bug -- Jobs.tsx happened to avoid
  // it by keying its fetch effect on primitives instead of a callback identity.
  const range: [number, number] | undefined = useMemo(
    () => (pageSize === 'all' ? undefined : [page * pageSize, page * pageSize + pageSize - 1]),
    [page, pageSize]
  )

  return { page, setPage, pageSize, setPageSize, range }
}
